import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EventModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  event, 
  language 
}) => {
  const [formData, setFormData] = useState(event || {
    title: '',
    titleAr: '',
    type: 'appointment',
    date: '',
    time: '',
    location: '',
    locationAr: '',
    participants: '',
    participantsAr: '',
    caseReference: '',
    courtroom: '',
    notes: '',
    notesAr: '',
    recurring: 'none'
  });

  const labels = {
    title: {
      en: event ? 'Edit Event' : 'Create Event',
      fr: event ? 'Modifier l\'événement' : 'Créer un événement',
      ar: event ? 'تعديل الحدث' : 'إنشاء حدث'
    },
    titleField: {
      en: 'Event Title',
      fr: 'Titre de l\'événement',
      ar: 'عنوان الحدث'
    },
    type: {
      en: 'Event Type',
      fr: 'Type d\'événement',
      ar: 'نوع الحدث'
    },
    date: {
      en: 'Date',
      fr: 'Date',
      ar: 'التاريخ'
    },
    time: {
      en: 'Time',
      fr: 'Heure',
      ar: 'الوقت'
    },
    location: {
      en: 'Location',
      fr: 'Lieu',
      ar: 'الموقع'
    },
    participants: {
      en: 'Participants',
      fr: 'Participants',
      ar: 'المشاركون'
    },
    caseReference: {
      en: 'Case Reference',
      fr: 'Référence du dossier',
      ar: 'مرجع القضية'
    },
    courtroom: {
      en: 'Courtroom',
      fr: 'Salle d\'audience',
      ar: 'قاعة المحكمة'
    },
    notes: {
      en: 'Notes',
      fr: 'Notes',
      ar: 'ملاحظات'
    },
    recurring: {
      en: 'Recurring',
      fr: 'Récurrent',
      ar: 'متكرر'
    },
    save: {
      en: 'Save Event',
      fr: 'Enregistrer',
      ar: 'حفظ الحدث'
    },
    cancel: {
      en: 'Cancel',
      fr: 'Annuler',
      ar: 'إلغاء'
    },
    delete: {
      en: 'Delete Event',
      fr: 'Supprimer',
      ar: 'حذف الحدث'
    }
  };

  const getLabel = (key) => labels?.[key]?.[language] || labels?.[key]?.en;

  const eventTypes = [
    { value: 'hearing', label: language === 'fr' ? 'Audience' : language === 'ar' ? 'جلسة' : 'Court Hearing' },
    { value: 'appointment', label: language === 'fr' ? 'Rendez-vous' : language === 'ar' ? 'موعد' : 'Client Appointment' },
    { value: 'deadline', label: language === 'fr' ? 'Échéance' : language === 'ar' ? 'موعد نهائي' : 'Deadline' },
    { value: 'meeting', label: language === 'fr' ? 'Réunion' : language === 'ar' ? 'اجتماع' : 'Internal Meeting' }
  ];

  const recurringOptions = [
    { value: 'none', label: language === 'fr' ? 'Aucun' : language === 'ar' ? 'لا يوجد' : 'None' },
    { value: 'daily', label: language === 'fr' ? 'Quotidien' : language === 'ar' ? 'يومي' : 'Daily' },
    { value: 'weekly', label: language === 'fr' ? 'Hebdomadaire' : language === 'ar' ? 'أسبوعي' : 'Weekly' },
    { value: 'monthly', label: language === 'fr' ? 'Mensuel' : language === 'ar' ? 'شهري' : 'Monthly' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-lg border border-border shadow-elevation-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-foreground">
            {getLabel('title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
          <Input
            label={getLabel('titleField')}
            type="text"
            value={formData?.title}
            onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
            required
          />

          {language === 'ar' && (
            <Input
              label={`${getLabel('titleField')} (العربية)`}
              type="text"
              value={formData?.titleAr}
              onChange={(e) => setFormData({ ...formData, titleAr: e?.target?.value })}
            />
          )}

          <Select
            label={getLabel('type')}
            options={eventTypes}
            value={formData?.type}
            onChange={(value) => setFormData({ ...formData, type: value })}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={getLabel('date')}
              type="date"
              value={formData?.date}
              onChange={(e) => setFormData({ ...formData, date: e?.target?.value })}
              required
            />

            <Input
              label={getLabel('time')}
              type="time"
              value={formData?.time}
              onChange={(e) => setFormData({ ...formData, time: e?.target?.value })}
              required
            />
          </div>

          <Input
            label={getLabel('location')}
            type="text"
            value={formData?.location}
            onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
          />

          {language === 'ar' && (
            <Input
              label={`${getLabel('location')} (العربية)`}
              type="text"
              value={formData?.locationAr}
              onChange={(e) => setFormData({ ...formData, locationAr: e?.target?.value })}
            />
          )}

          <Input
            label={getLabel('participants')}
            type="text"
            value={formData?.participants}
            onChange={(e) => setFormData({ ...formData, participants: e?.target?.value })}
          />

          {formData?.type === 'hearing' && (
            <>
              <Input
                label={getLabel('caseReference')}
                type="text"
                value={formData?.caseReference}
                onChange={(e) => setFormData({ ...formData, caseReference: e?.target?.value })}
              />

              <Input
                label={getLabel('courtroom')}
                type="text"
                value={formData?.courtroom}
                onChange={(e) => setFormData({ ...formData, courtroom: e?.target?.value })}
              />
            </>
          )}

          <Select
            label={getLabel('recurring')}
            options={recurringOptions}
            value={formData?.recurring}
            onChange={(value) => setFormData({ ...formData, recurring: value })}
          />

          <Input
            label={getLabel('notes')}
            type="text"
            value={formData?.notes}
            onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
          />

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
              className="flex-1"
            >
              {getLabel('save')}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {getLabel('cancel')}
            </Button>

            {event && (
              <Button
                type="button"
                variant="destructive"
                iconName="Trash2"
                onClick={() => {
                  if (window.confirm(language === 'fr' ? 'Supprimer cet événement?' : language === 'ar' ? 'حذف هذا الحدث؟' : 'Delete this event?')) {
                    onClose();
                  }
                }}
              >
                {getLabel('delete')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;